
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { UserCheck, UserX, User, ShieldCheck, ShieldX, ShieldAlert, AlertTriangle } from 'lucide-react';

interface UserProfile {
  id: string;
  username: string | null;
  email: string | null;
  full_name: string | null;
  role: string;
  is_blocked: boolean;
  is_store: boolean;
  store_approval_status: 'pending' | 'approved' | 'rejected' | null;
  store_name: string | null;
}

type ActionType = 'block' | 'unblock' | 'makeAdmin' | 'removeAdmin' | 'approveStore' | 'rejectStore' | 'markPending' | 'convertToUser';

interface UserActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (adminNotes: string) => void;
  user: UserProfile | null;
  actionType: ActionType | null;
  isProcessing: boolean;
}

const UserActionDialog = ({
  isOpen,
  onClose,
  onConfirm,
  user,
  actionType,
  isProcessing
}: UserActionDialogProps) => {
  const [adminNotes, setAdminNotes] = useState('');

  const handleConfirm = () => {
    onConfirm(adminNotes);
    setAdminNotes('');
  };

  const handleClose = () => {
    onClose();
    setAdminNotes('');
  };

  if (!user || !actionType) return null;

  const getActionConfig = () => {
    switch (actionType) {
      case 'block':
        return {
          title: 'Block User',
          description: 'Are you sure you want to block this user? They will not be able to access the platform.',
          icon: <UserX className="h-5 w-5 text-red-500" />,
          confirmLabel: 'Block User',
          isDangerous: true,
          notesLabel: 'Reason for blocking (optional)',
          notesPlaceholder: 'Enter the reason for blocking this user...'
        };
      case 'unblock':
        return {
          title: 'Unblock User',
          description: 'Are you sure you want to unblock this user? They will regain access to the platform.',
          icon: <UserCheck className="h-5 w-5 text-green-500" />,
          confirmLabel: 'Unblock User',
          isDangerous: false,
          notesLabel: 'Admin notes (optional)',
          notesPlaceholder: 'Add any notes about unblocking this user...'
        };
      case 'makeAdmin':
        return {
          title: 'Grant Admin Rights',
          description: 'Are you sure you want to make this user an administrator? They will have full access to admin features.',
          icon: <UserCheck className="h-5 w-5 text-blue-500" />,
          confirmLabel: 'Make Admin',
          isDangerous: true,
          notesLabel: 'Admin notes (optional)',
          notesPlaceholder: 'Add any notes about granting admin rights...'
        };
      case 'removeAdmin':
        return {
          title: 'Remove Admin Rights',
          description: 'Are you sure you want to remove admin rights from this user?',
          icon: <User className="h-5 w-5 text-orange-500" />,
          confirmLabel: 'Remove Admin Rights',
          isDangerous: true,
          notesLabel: 'Admin notes (optional)',
          notesPlaceholder: 'Add any notes about removing admin rights...'
        };
      case 'approveStore':
        return {
          title: 'Approve Store',
          description: 'Are you sure you want to approve this store application?',
          icon: <ShieldCheck className="h-5 w-5 text-green-500" />,
          confirmLabel: 'Approve Store',
          isDangerous: false,
          notesLabel: 'Approval notes (optional)',
          notesPlaceholder: 'Add any notes about the store approval...'
        };
      case 'rejectStore':
        return {
          title: 'Reject Store',
          description: 'Are you sure you want to reject this store application?',
          icon: <ShieldX className="h-5 w-5 text-red-500" />,
          confirmLabel: 'Reject Store',
          isDangerous: true,
          notesLabel: 'Rejection reason (recommended)',
          notesPlaceholder: 'Enter the reason for rejecting this store application...'
        };
      case 'markPending':
        return {
          title: 'Mark Store as Pending',
          description: 'Are you sure you want to mark this store status as pending?',
          icon: <ShieldAlert className="h-5 w-5 text-yellow-500" />,
          confirmLabel: 'Mark as Pending',
          isDangerous: false,
          notesLabel: 'Admin notes (optional)',
          notesPlaceholder: 'Add any notes about marking as pending...'
        };
      case 'convertToUser':
        return {
          title: 'Convert to Regular User',
          description: 'Are you sure you want to convert this store back to a regular user? This will remove all store-related data.',
          icon: <User className="h-5 w-5 text-orange-500" />,
          confirmLabel: 'Convert to User',
          isDangerous: true,
          notesLabel: 'Conversion reason (recommended)',
          notesPlaceholder: 'Enter the reason for converting to regular user...'
        };
      default:
        return {
          title: 'Confirm Action',
          description: 'Are you sure you want to perform this action?',
          icon: <AlertTriangle className="h-5 w-5" />,
          confirmLabel: 'Confirm',
          isDangerous: false,
          notesLabel: 'Admin notes',
          notesPlaceholder: 'Add your notes...'
        };
    }
  };

  const config = getActionConfig();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${config.isDangerous ? 'text-destructive' : ''}`}>
            {config.icon}
            {config.title}
          </DialogTitle>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Information */}
          <div className="p-3 bg-muted rounded-md">
            <h4 className="font-medium text-sm mb-2">User Information:</h4>
            <div className="space-y-1">
              <p className="font-semibold">{user.full_name || 'No Name'}</p>
              <p className="text-sm text-muted-foreground">
                {user.username ? '@' + user.username : ''} • {user.email}
              </p>
              <div className="flex gap-2 mt-2">
                <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                  {user.role === 'admin' ? 'Admin' : 'User'}
                </Badge>
                {user.is_blocked && <Badge variant="destructive">Blocked</Badge>}
                {user.store_approval_status === 'approved' && (
                  <Badge className="bg-green-100 text-green-700">Approved Store</Badge>
                )}
                {user.store_approval_status === 'pending' && (
                  <Badge className="bg-yellow-100 text-yellow-700">Pending Store</Badge>
                )}
              </div>
              {user.store_name && (
                <p className="text-sm mt-1">
                  <strong>Store:</strong> {user.store_name}
                </p>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <h4 className="font-medium mb-2">{config.notesLabel}</h4>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={config.notesPlaceholder}
              rows={3}
            />
          </div>

          {/* Warning for dangerous actions */}
          {config.isDangerous && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-sm text-destructive">
                <strong>Warning:</strong> This action may have significant consequences. Please ensure you understand the implications before proceeding.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant={config.isDangerous ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : config.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserActionDialog;
